// ─── CV / Cover Letter data model for /marko ────────────────────────────────
// Single source of truth, transcribed from Marko Petrovski's CV verbatim.

export interface Experience {
    id: string;
    role: string;
    org: string;
    period: string;
    current?: boolean;   // the active job — filled timeline dot
    bullets: string[];   // verbatim from CV
}

export interface Engagement {
    name: string;
    role: string;
    period: string;
    icon: string;
    url?: string;
    badge?: string;      // e.g. flag a product built by Marko
    bullets: string[];   // verbatim from CV
}

export interface SkillGroup {
    label: string;
    icon: string;
    skills: string[];
}

export const PROFILE = {
    name: 'Marko Petrovski',
    title: 'Lead Android Engineer',
    subtitle: 'Lead Engineer, Android Platforms @ Fueled · Founder',
    location: 'Worldwide · Remote',
    email: 'markopetrovskimp@gmail.com',
    roles: [
        'Lead Android Engineer',
        'Kotlin Multiplatform Architect',
        'Jetpack Compose Specialist',
        'Compose Multiplatform Developer',
        'Flutter Engineer',
    ],
    tagline:
        'I design and deliver scalable mobile applications across healthcare, fintech, IoT, media and consumer products — specializing in Android, Flutter, Kotlin Multiplatform, Compose Multiplatform, Kotlin, Jetpack Compose, and modern Android architecture.',
};

export const LINKS = {
    github: 'https://github.com/PetrovskiM',
    linkedin: 'https://www.linkedin.com/in/marko-petrovski/',
    site: 'https://medimystery.com',
};

export const STATS = [
    { value: 8, suffix: '+', label: 'Years Experience' },
    { value: 8, suffix: '', label: 'Companies & Clients' },
    { value: 5, suffix: '', label: 'Industries' },
    { value: 2, suffix: '', label: 'Products Built' },
];

// Cover letter, split into paragraphs for staged reveal.
export const COVER_LETTER: string[] = [
    'I am a Lead Android Engineer and Independent Android Developer with over 8 years of experience building and leading development of scalable mobile applications across healthcare, fintech, streaming, IoT, and consumer products. I specialize in architecting reliable, maintainable systems and helping teams deliver high-quality products in remote, fast-paced environments.',
    'In my recent roles, I have led engineering efforts on complex applications involving real-time BLE communication and mapping with dog collars, led two teams and products in the entertainment and streaming industries, driven a Kotlin Multiplatform shared-architecture migration, and delivered modern Android development with Jetpack Compose and Flutter games for children. My responsibilities have included defining technical direction, leading migrations from legacy codebases, mentoring engineers, and collaborating closely with product and design teams to deliver impactful user-facing features.',
    'In addition to my professional work, I independently design, develop, and publish Android applications on Google Play. This has strengthened my product mindset, ownership, and ability to drive products from idea to production.',
    'Having worked with fully remote teams across US and European companies, I am comfortable operating autonomously, contributing to technical decisions, and delivering consistently in distributed environments.',
    'I would welcome the opportunity to contribute my experience and help your team build high-quality mobile products. Thank you for your time and consideration, and I look forward to speaking with you.',
];

export const EXPERIENCE: Experience[] = [
    {
        id: 'fueled-lead',
        role: 'Lead Engineer, Android Platforms',
        org: 'Fueled',
        period: 'August 2025 — Present',
        current: true,
        bullets: [
            'Lead Android and Flutter project development across full SDLC, from architecture to production release',
            'Established internal guidelines and templates for AI-assisted Flutter development, improving team efficiency',
            'Conduct technical interviews and participate in hiring decisions',
            'Present technical solutions and guide clients on architecture and platform',
        ],
    },
    {
        id: 'fueled-senior',
        role: 'Senior Android Engineer | Consultant',
        org: 'Fueled',
        period: 'December 2024 — July 2025',
        bullets: [
            'Contributed to all phases of software development lifecycle (SDLC), adapting to specific client needs and workflows.',
            'Led architectural design discussions and implemented scalable, maintainable solutions',
            'Led development of real-time Android features using Kotlin and Coroutines, integrating and combining data from BLE hardware, WebSockets, REST polling, and location data into responsive user-facing experiences',
            'Communicated technical tradeoffs and recommendations to stakeholders',
        ],
    },
    {
        id: 'deltatre',
        role: 'Senior and acting Lead Android Engineer',
        org: 'Deltatre',
        period: 'July 2024 — March 2026',
        bullets: [
            'Led development of Android SDK across Android Mobile, Android TV, and Fire TV using Kotlin and modern Android architecture',
            'Be main technical point of contact across teams and projects',
            'Defined technical direction and architecture',
            'Own feature delivery, collaborating with product and project teams to ensure timely releases',
            'Refactored legacy codebase to modern Android architecture, improving scalability, maintainability, and performance, eliminating ANRs, reducing memory leaks by 90%, and improving responsiveness by 50–100 ms.',
            'Develop new features with a user-centric, pragmatic approach.',
            'Manage technical debt and roadmaps; conduct technology evaluations and product spikes.',
            'Act as liaison between engineering and stakeholders, present progress, clarify requirements, gather feedback and guide development.',
            'Mentored engineers and conducted technical interviews',
        ],
    },
    {
        id: 'seventech-consultant',
        role: 'Senior Android Engineer | Consultant',
        org: 'Seventech GmbH',
        period: 'February 2024 — September 2024',
        bullets: [
            'Led migration of Android and iOS architecture to a shared Kotlin Multiplatform codebase, defining shared boundaries and improving cross-platform code reuse and maintainability.',
            'Planned and delivered new features, improved architecture, and modernized legacy implementations.',
        ],
    },
    {
        id: 'fueled-senior-mobile',
        role: 'Senior Mobile Engineer, Android',
        org: 'Fueled',
        period: 'January 2022 — October 2023',
        bullets: [
            'Developed large-scale Android application using Kotlin Multiplatform, Jetpack Compose, and modern Android architecture',
            'Led architectural decisions and introduced modern Android technologies',
            'Mentored junior engineers and contributed to technical leadership',
            'Integrated advanced features including barcode scanning, camera, analytics, and backend integrations',
        ],
    },
    {
        id: 'devsy',
        role: 'Android Engineer',
        org: 'Devsy / Coniq',
        period: 'April 2020 — January 2022',
        bullets: [
            'Developed large-scale Android application using Kotlin Multiplatform, Jetpack Compose, and modern Android architecture',
            'Led architectural decisions and introduced modern Android technologies',
            'Mentored junior engineers and contributed to technical leadership',
            'Integrated advanced features including barcode scanning, camera, analytics, and backend integrations',
        ],
    },
    {
        id: 'webfactory',
        role: 'Android Engineer',
        org: 'WebFactory',
        period: 'September 2018 — April 2020',
        bullets: [
            'Lead Android developer on multiple client projects',
            'Built and maintained production Android applications',
            'Mentored junior engineers and interns',
            'Researched technologies such as Flutter and Node.js and their adaptation into projects.',
        ],
    },
];

// PERSONAL ENGAGEMENTS & PROJECTS — verbatim from CV.
export const ENGAGEMENTS: Engagement[] = [
    {
        name: 'MediMystery',
        role: 'Independent Android Engineer',
        period: 'May 2024 — Present',
        icon: 'stethoscope',
        url: 'https://medimystery.com',
        badge: 'Product I built',
        bullets: [
            'Built cross-platform app using Compose Multiplatform and MERN stack',
            'Designed scalable architecture across mobile, web, and backend',
            'Led feature development, UI/UX design, and roadmap planning from an engineering and product perspective',
            'Executed marketing strategies to grow user adoption',
        ],
    },
    {
        name: 'Repeatable',
        role: 'Independent Android Engineer',
        period: 'October 2023 — Present',
        icon: 'apps',
        url: 'https://play.google.com/store/apps/details?id=com.repeatable',
        badge: 'Product I built',
        bullets: [
            'Built cross-platform app using Compose Multiplatform and MERN stack',
            'Designed scalable architecture across mobile, web, and backend',
            'Led feature development, UI/UX design, and roadmap planning from an engineering and product perspective',
            'Executed marketing strategies to grow user adoption',
        ],
    },
    {
        name: 'Program Ceo',
        role: 'Senior Android Engineer | Freelance',
        period: 'January 2021 — Present',
        icon: 'code_blocks',
        bullets: [
            'Built cross-platform app using Compose Multiplatform and MERN stack',
            'Designed scalable architecture across mobile, web, and backend',
            'Led feature development, UI/UX design, and roadmap planning from an engineering and product perspective',
            'Executed marketing strategies to grow user adoption',
        ],
    },
];

// SKILLS — one card per CV category, verbatim.
export const SKILL_GROUPS: SkillGroup[] = [
    {
        label: 'Languages',
        icon: 'code',
        skills: ['Kotlin', 'Dart', 'Java', 'TypeScript', 'SwiftUI'],
    },
    {
        label: 'Cross-Platform',
        icon: 'devices',
        skills: ['Kotlin Multiplatform', 'Compose Multiplatform', 'Flutter'],
    },
    {
        label: 'Android',
        icon: 'android',
        skills: [
            'Android SDK', 'Android Jetpack', 'Lifecycle', 'ViewModel', 'Navigation',
            'Navigation 3', 'WorkManager', 'Jetpack Compose', 'Android TV', 'Fire TV',
            'Google Maps SDK', 'XML/Views',
        ],
    },
    {
        label: 'Kotlin',
        icon: 'bolt',
        skills: ['Coroutines', 'Flow', 'StateFlow', 'SharedFlow', 'Kotlin Serialization', 'KSP', 'Kotlin DSL'],
    },
    {
        label: 'Architecture',
        icon: 'architecture',
        skills: [
            'MVVM', 'MVI', 'MVP', 'Modularization', 'Clean Architecture', 'BLoC',
            'Feature-based Architecture', 'SOLID', 'Design patterns',
        ],
    },
    {
        label: 'Dependency Injection',
        icon: 'account_tree',
        skills: ['Hilt', 'Dagger', 'Koin'],
    },
    {
        label: 'Networking',
        icon: 'lan',
        skills: ['REST', 'GraphQL', 'Retrofit', 'OkHttp', 'WebSockets'],
    },
    {
        label: 'Realtime / Hardware',
        icon: 'sensors',
        skills: ['BLE', 'WebSockets', 'Beacons', 'Location Services'],
    },
    {
        label: 'Database / Storage',
        icon: 'database',
        skills: ['Room', 'SQLite', 'DataStore'],
    },
    {
        label: 'Media',
        icon: 'smart_display',
        skills: ['ExoPlayer', 'Media3'],
    },
    {
        label: 'Build / CI/CD',
        icon: 'build',
        skills: ['Gradle', 'Gradle Convention Plugins', 'Github Actions', 'Codemagic', 'Fastlane'],
    },
    {
        label: 'Backend / Web',
        icon: 'dns',
        skills: ['Node.js', 'React', 'TypeScript', 'MERN'],
    },
    {
        label: 'Testing',
        icon: 'science',
        skills: ['JUnit', 'Unit Testing', 'Integration Testing', 'Mockk', 'Mockito'],
    },
    {
        label: 'Tools',
        icon: 'construction',
        skills: [
            'Firebase Analytics', 'Crashlytics', 'Remote Config', 'Cloud Messaging',
            'Cloud Functions', 'Supabase suite', 'Amplitude', 'Revenuecat',
        ],
    },
];

export const INDUSTRIES = [
    'Healthcare', 'Fintech', 'IoT', 'Media & Streaming', 'Consumer',
];

export const EDUCATION = {
    degree: 'Bachelor of Engineer in Information Science and Computer Engineering',
    major: 'Majoring in Computer Science and Engineering',
};
